import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type ChangeEvent,
} from "react";
import { Clock, Plus, Timer } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { notify } from "@/components/common/Notification";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  DynamicModuleService,
  SchemaService,
  PtsSpentHoursService,
  PtsProjectService,
  PtsEmployeeService,
  PtsRemainingTimeRequestService,
} from "@/services/APIs";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { getEmployeeId } from "@/lib/storeManager";
import { get } from "@/services/axiosUitils";
import { Modal, DatePicker, Form, Select, Input } from "antd";
import dayjs from "dayjs";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { buildSpentHoursStatusOptions } from "./spentHoursStatusHelpers";
import { SpentHoursSubtaskModal } from "./SpentHoursSubtaskModal";

const SPENT_HOURS_SCHEMA_NAME = "PTS Spent Hours";
// const TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";

type ProjectTaskOption = {
  value: string;
  label: string;
  /** True when task name is "Other" (dashboard list): free-text field instead of sub-task API */
  isOther: boolean;
  /** From project-task API `estimatedhours` (read-only display next to task) */
  estimatedhours?: number | null;
  /** From project-task API `status` (task workflow id) */
  status?: string | null;
  /** From project-task API `worked_hours` (e.g. "7:00") */
  worked_hours?: string | null;
};

type SubtaskOption = {
  value: string;
  label: string;
  estimatedhours?: number | null;
  assignedEmployeeIds?: string[];
  /** From sub-task API `worked_hours` (e.g. "0:00") */
  worked_hours?: string | null;
  /** From sub-task API `servicedesk_uid` (e.g. "TXT-10448") */
  servicedesk_uid?: string | null;
};

/** Parent task estimate until a sub-task is chosen; then sub-task estimate (matches UI field). */
function resolveDisplayedEstimatedHours(
  taskId: unknown,
  subTaskValue: unknown,
  taskOptions: ProjectTaskOption[] | undefined,
  subtaskOptions: SubtaskOption[] | undefined,
  subtasksLoading: boolean
): number | null {
  if (taskId === undefined || taskId === null || String(taskId).trim() === "") {
    return null;
  }
  if (!taskOptions?.length) {
    return null;
  }
  const task = taskOptions.find(x => String(x.value) === String(taskId));
  if (!task || task.isOther) {
    return null;
  }

  const hasSubtaskUi = !subtasksLoading && (subtaskOptions?.length ?? 0) > 0;
  const hasSubSelection =
    hasSubtaskUi &&
    subTaskValue !== undefined &&
    subTaskValue !== null &&
    String(subTaskValue).trim() !== "";

  if (hasSubSelection) {
    const st = subtaskOptions?.find(
      o => String(o.value) === String(subTaskValue)
    );
    return st?.estimatedhours ?? null;
  }
  return task.estimatedhours ?? null;
}

/** Worked hours label from APIs (H:MM); mirrors estimated parent vs sub-task selection. */
function resolveDisplayedWorkedHoursFromApis(
  taskId: unknown,
  subTaskValue: unknown,
  taskOptions: ProjectTaskOption[] | undefined,
  subtaskOptions: SubtaskOption[] | undefined,
  subtasksLoading: boolean
): string | null {
  if (taskId === undefined || taskId === null || String(taskId).trim() === "") {
    return null;
  }
  if (!taskOptions?.length) {
    return null;
  }
  const task = taskOptions.find(x => String(x.value) === String(taskId));
  if (!task || task.isOther) {
    return null;
  }

  const hasSubtaskUi = !subtasksLoading && (subtaskOptions?.length ?? 0) > 0;
  const hasSubSelection =
    hasSubtaskUi &&
    subTaskValue !== undefined &&
    subTaskValue !== null &&
    String(subTaskValue).trim() !== "";

  if (hasSubSelection) {
    const st = subtaskOptions?.find(
      o => String(o.value) === String(subTaskValue)
    );
    const wh = st?.worked_hours;
    if (wh !== undefined && wh !== null && String(wh).trim() !== "") {
      return String(wh).trim();
    }
    return null;
  }

  const wh = task.worked_hours;
  if (wh !== undefined && wh !== null && String(wh).trim() !== "") {
    return String(wh).trim();
  }
  return null;
}

/** Entry hours + minutes as H:MM for display when API has no aggregate (e.g. "Other"). */
function formatEntryHoursMinutesLabel(
  hoursField: unknown,
  minutesField: unknown
): string {
  const hoursValue = hoursField ?? "00";
  const minutesValue = minutesField ?? "00";
  const hours =
    hoursValue === "" || hoursValue === null || hoursValue === undefined
      ? 0
      : Number(hoursValue) || 0;
  const minutes =
    minutesValue === "" || minutesValue === null || minutesValue === undefined
      ? 0
      : Number(minutesValue) || 0;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

/** Remove leading/trailing empty paragraphs and whitespace from Quill HTML */
function trimQuillContent(html: string): string {
  if (!html || typeof html !== "string") {
    return html;
  }
  let s = html.trim();
  const emptyTag = (tag: string) =>
    new RegExp(
      `^<${tag}(\\s[^>]*)?>(\\s|&nbsp;|<br\\s*\\/?>)*<\\/${tag}>\\s*|\\s*<${tag}(\\s[^>]*)?>(\\s|&nbsp;|<br\\s*\\/?>)*<\\/${tag}>$`,
      "gi"
    );
  ["p", "div"].forEach(tag => {
    let prev;
    do {
      prev = s;
      s = s.replace(emptyTag(tag), "");
    } while (prev !== s);
  });
  return s.trim();
}

const TASK_NAME_DESC_MAX_LEN = 50;
const DESCRIPTION_MIN_CHARS = 25;
const DESCRIPTION_MAX_CHARS = 150;

const TOTAL_WORKING_MINUTES = 9 * 60;
const TOTAL_WORKING_HOURS_LABEL = "9:00";
/** Modal only when productive time is at or below 8 hours */
const PRODUCTIVE_HOURS_MODAL_MAX_MINUTES = 8 * 60;
const REMAINING_TIME_MODAL_THRESHOLD_MINUTES = 30;
const REMAINING_TIME_REASON_MIN_CHARS = 10;
const REMAINING_TIME_REASON_MAX_CHARS = 100;

function validateRemainingTimeReason(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return "Reason is required.";
  }
  if (trimmed.length < REMAINING_TIME_REASON_MIN_CHARS) {
    return `Reason must be at least ${REMAINING_TIME_REASON_MIN_CHARS} characters.`;
  }
  if (trimmed.length > REMAINING_TIME_REASON_MAX_CHARS) {
    return `Reason must not exceed ${REMAINING_TIME_REASON_MAX_CHARS} characters.`;
  }
  return null;
}

function isWeekend(date: dayjs.Dayjs): boolean {
  const day = date.day();
  return day === 0 || day === 6;
}

function parseProductiveHoursToMinutes(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  const text = String(value).trim().toLowerCase();
  if (!text || text === "-" || text === "--") {
    return 0;
  }

  const colonMatch = text.match(/^(\d{1,2}):(\d{1,2})(?:\s*h)?$/);
  if (colonMatch) {
    const h = Number(colonMatch[1]);
    const m = Number(colonMatch[2]);
    if (Number.isFinite(h) && Number.isFinite(m)) {
      return h * 60 + m;
    }
  }

  const hoursMinutesMatch = text.match(
    /^(\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m)?$/
  );
  if (hoursMinutesMatch) {
    const h = Number(hoursMinutesMatch[1]) || 0;
    const m = Number(hoursMinutesMatch[2]) || 0;
    return Math.round(h * 60) + m;
  }

  const hoursPart = text.match(/(\d+(?:\.\d+)?)\s*h/);
  const minutesPart = text.match(/(\d+)\s*m/);
  if (hoursPart || minutesPart) {
    const h = hoursPart ? Number(hoursPart[1]) : 0;
    const m = minutesPart ? Number(minutesPart[1]) : 0;
    if (Number.isFinite(h) && Number.isFinite(m)) {
      return Math.round(h * 60) + m;
    }
  }

  const numeric = Number(text.replace(/h/gi, "").trim());
  if (Number.isFinite(numeric)) {
    return Math.round(numeric * 60);
  }

  return 0;
}

function formatMinutesToHHMM(totalMinutes: number): string {
  const safeMinutes = Math.max(0, totalMinutes);
  const h = Math.floor(safeMinutes / 60);
  const m = safeMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatRemainingTimeDisplay(totalMinutes: number): string {
  const safeMinutes = Math.max(0, totalMinutes);
  const h = Math.floor(safeMinutes / 60);
  const m = safeMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function calculateRemainingTimeFromProductiveHours(productiveMinutes: number): {
  payload: string;
  display: string;
  productiveDisplay: string;
  remainingMinutes: number;
} | null {
  if (productiveMinutes >= TOTAL_WORKING_MINUTES) {
    return null;
  }
  if (productiveMinutes > PRODUCTIVE_HOURS_MODAL_MAX_MINUTES) {
    return null;
  }
  const remainingMinutes = TOTAL_WORKING_MINUTES - productiveMinutes;
  return {
    payload: formatMinutesToHHMM(remainingMinutes),
    display: formatRemainingTimeDisplay(remainingMinutes),
    productiveDisplay: formatRemainingTimeDisplay(productiveMinutes),
    remainingMinutes,
  };
}

type PendingRemainingTimeSubmit = {
  values: Record<string, unknown>;
  payload: Record<string, unknown>;
  formattedDate: string;
  remainingTime: string;
  remainingTimeDisplay: string;
  productiveTimeDisplay: string;
};

async function getTodaySpentHoursCount(
  employeeId: string,
  date: dayjs.Dayjs
): Promise<number> {
  const dateStr = date.format("YYYY-MM-DD");
  const response = await DynamicModuleService.getItemsByPage(
    SPENT_HOURS_SCHEMA_NAME,
    1,
    1,
    {
      date__gte: `${dateStr}T00:00:00`,
      date__lte: `${dateStr}T23:59:59`,
      emp_id: employeeId,
    }
  );

  if (typeof response?.count === "number") {
    return response.count;
  }
  if (Array.isArray(response?.results)) {
    return response.results.length;
  }
  return 0;
}

// function countWordsFromHtml(html: string): number {
//   if (!html || typeof html !== "string") {
//     return 0;
//   }
//   if (typeof document === "undefined") {
//     return 0;
//   }
//   const tmp = document.createElement("div");
//   tmp.innerHTML = html;
//   const text = (tmp.textContent ?? "").replace(/\u00a0/g, " ").trim();
//   if (!text) {
//     return 0;
//   }
//   return text.split(/\s+/).filter(Boolean).length;
// }

/** Visible/plain-text length for Quill HTML (tags and attributes must not count toward the limit). */
function countCharsFromHtml(html: string): number {
  if (!html || typeof html !== "string") {
    return 0;
  }
  const trimmed = html.trim();
  if (!trimmed) {
    return 0;
  }
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = trimmed;
    const text = (tmp.textContent ?? "").replace(/\u00a0/g, " ").trim();
    return text.length;
  }
  const text = trimmed
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  return text.length;
}

function isCompletedStatusValue(
  statusValue: unknown,
  options: Array<{ value: string; label: string }>
): boolean {
  if (
    statusValue === undefined ||
    statusValue === null ||
    String(statusValue).trim() === ""
  ) {
    return false;
  }
  const sv = String(statusValue);
  const completedOption = options.find(
    o => String(o.label).trim().toLowerCase() === "completed"
  );
  if (completedOption && String(completedOption.value) === sv) {
    return true;
  }
  const normalized = sv.trim().toLowerCase();
  if (normalized === "completed" || normalized === "completd") {
    return true;
  }
  // Backend/data may occasionally send near variants like "completd".
  // Treat any "complet*" token as completed for workflow checks.
  return /^complet[a-z]*$/.test(normalized);
}

const AddSpentHoursTab = () => {
  const queryClient = useQueryClient();
  const employeeId = getEmployeeId();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasPrefilledFields, setHasPrefilledFields] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [remainingTimeModalVisible, setRemainingTimeModalVisible] =
    useState(false);
  const [remainingTimeReason, setRemainingTimeReason] = useState("");
  const [remainingTimeReasonError, setRemainingTimeReasonError] = useState<
    string | null
  >(null);
  const [pendingRemainingTimeSubmit, setPendingRemainingTimeSubmit] =
    useState<PendingRemainingTimeSubmit | null>(null);
  const [subtaskModalOpen, setSubtaskModalOpen] = useState(false);
  const quillEditorRef = useRef<ReactQuill>(null);

  // Generate hours options (0-23)
  const hoursOptions = useMemo(() => {
    return Array.from({ length: 16 }, (_, i) => ({
      value: String(i).padStart(2, "0"),
      label: `${String(i).padStart(2, "0")} Hrs.`,
    }));
  }, []);

  // Generate minutes options (0-59)
  const minutesOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const value = i * 5;
      return {
        value: String(value).padStart(2, "0"),
        label: `${String(value).padStart(2, "0")} Min.`,
      };
    });
  }, []);

  // Fetch Formily schema (inline enums e.g. status live under schema.json, not drop_down_data)
  const { data: spentHoursSchemaRecord, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(SPENT_HOURS_SCHEMA_NAME),
    enabled: !!SPENT_HOURS_SCHEMA_NAME,
  });

  // Fetch column grid data to find field names
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", SPENT_HOURS_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SPENT_HOURS_SCHEMA_NAME),
  });

  const { data: dashboardData } = useQuery({
    queryKey: ["employee-dashboard", employeeId],
    queryFn: () => PtsEmployeeService.getEmployeeDashboard(employeeId!),
    enabled: !!employeeId,
  });

  // Find field names from schema
  const fieldNames = useMemo(() => {
    if (!columnGridData?.grid) {
      return {
        project: "spentproject",
        task: "spenttask",
        category: "category",
        date: "date",
        hours: "hours",
        description: "description",
        subTask: "sub_task",
        status: "status",
        estimatedHours: "estimatedhours",
      };
    }

    const findFieldName = (
      searchTerms: string[],
      excludeNameSubstrings: string[] = []
    ) => {
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          excludeNameSubstrings.some(
            ex => fieldName.includes(ex) ?? fieldTitle.includes(ex)
          )
        ) {
          continue;
        }
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name;
        }
      }
      return null;
    };

    return {
      project: findFieldName(["project", "spentproject"]) ?? "spentproject",
      task: findFieldName(["task", "spenttask"]) ?? "spenttask",
      category: findFieldName(["category", "task_category"]) ?? "category",
      date: findFieldName(["date", "work_date", "entry_date"]) ?? "date",
      // Exclude "estimate*" so `estimatedhours` is not mistaken for spent `hours`
      hours:
        findFieldName(["spent_hours", "total_hours", "hours"], ["estimate"]) ??
        "hours",
      description: findFieldName(["description", "desc"]) ?? "description",
      subTask:
        findFieldName(["sub_task", "subtask", "spent_subtask"]) ?? "sub_task",
      status: findFieldName(["status", "approval_status"]) ?? "status",
      estimatedHours:
        findFieldName(["estimatedhours", "estimated_hours", "estimate"]) ??
        "estimatedhours",
    };
  }, [columnGridData]);

  // Same queryKey as ActivityLogTab when employeeId matches → one shared request (emp_id only)
  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list", employeeId],
    queryFn: () =>
      PtsProjectService
        .getOpenProjectList
        // employeeId ? { emp_id: employeeId } : {}
        (),
    enabled: !!employeeId,
  });

  // Fetch category dropdown
  // const { data: categoryOptions } = useQuery({
  //   queryKey: ["category-dropdown", TASK_CATEGORY_SCHEMA_NAME],
  //   queryFn: () => DropdownService.getDropdownData(TASK_CATEGORY_SCHEMA_NAME),
  //   enabled: !!TASK_CATEGORY_SCHEMA_NAME,
  // });

  // Status options: Formily schema inline enum (drop_down_data for this model often 404/errors on API)
  const spentHoursStatusOptions = useMemo(() => {
    return buildSpentHoursStatusOptions(
      spentHoursSchemaRecord,
      columnGridData?.grid,
      fieldNames.status
    );
  }, [spentHoursSchemaRecord, columnGridData, fieldNames.status]);

  // Fetch tasks by project (employee dashboard: includes "Other" and full task list)
  const { data: taskOptions, isLoading: tasksLoading } = useQuery({
    queryKey: ["project-task", "employee-dashboard", selectedProject],
    queryFn: async (): Promise<ProjectTaskOption[]> => {
      if (!selectedProject) {
        return [];
      }
      const projectId = String(selectedProject);
      const response = await get<any>("/pts_app/project-task/", {
        params: {
          project: projectId,
          is_dashboard: true,
        },
      });

      if (
        response?.success &&
        response?.tasks &&
        Array.isArray(response.tasks)
      ) {
        return response.tasks.map((task: any) => {
          const label =
            task.taskname ?? task.name ?? String(task.id ?? task.value);
          const isOther = String(label).trim().toLowerCase() === "other";
          const rawEst = task.estimatedhours ?? task.estimated_hours ?? null;
          const estimatedhours =
            rawEst === null || rawEst === undefined || rawEst === ""
              ? null
              : Number(rawEst);
          const rawWh = task.worked_hours ?? task.workedHours ?? null;
          const worked_hours =
            rawWh === undefined || rawWh === null || String(rawWh).trim() === ""
              ? null
              : String(rawWh).trim();
          const rawStatus = task.status ?? task.status_id ?? null;
          const status =
            rawStatus === undefined ||
            rawStatus === null ||
            String(rawStatus).trim() === ""
              ? null
              : String(rawStatus).trim();
          return {
            value: String(task.id ?? task.value),
            label,
            isOther,
            estimatedhours:
              estimatedhours !== null && !Number.isNaN(estimatedhours)
                ? estimatedhours
                : null,
            status,
            worked_hours,
          };
        });
      }

      if (Array.isArray(response)) {
        return response.map((task: any) => {
          const label =
            task.taskname ?? task.name ?? task.label ?? String(task.id);
          const isOther = String(label).trim().toLowerCase() === "other";
          const rawEst = task.estimatedhours ?? task.estimated_hours ?? null;
          const estimatedhours =
            rawEst === null || rawEst === undefined || rawEst === ""
              ? null
              : Number(rawEst);
          const rawWh = task.worked_hours ?? task.workedHours ?? null;
          const worked_hours =
            rawWh === undefined || rawWh === null || String(rawWh).trim() === ""
              ? null
              : String(rawWh).trim();
          const rawStatus = task.status ?? task.status_id ?? null;
          const status =
            rawStatus === undefined ||
            rawStatus === null ||
            String(rawStatus).trim() === ""
              ? null
              : String(rawStatus).trim();
          return {
            value: String(task.id ?? task.value),
            label,
            isOther,
            estimatedhours:
              estimatedhours !== null && !Number.isNaN(estimatedhours)
                ? estimatedhours
                : null,
            status,
            worked_hours,
          };
        });
      }

      return [];
    },
    enabled: !!selectedProject,
    placeholderData: undefined,
  });

  const watchedTaskId = Form.useWatch("spenttask", form);
  const watchedStatus = Form.useWatch("status", form);
  const watchedSubTaskId = Form.useWatch("sub_task", form);
  const lastKnownSubTaskIdRef = useRef<string>("");
  const [editRecordStatusFallback, setEditRecordStatusFallback] = useState("");
  const [editRecordSubTaskFallback, setEditRecordSubTaskFallback] =
    useState("");

  useEffect(() => {
    if (
      watchedSubTaskId !== undefined &&
      watchedSubTaskId !== null &&
      String(watchedSubTaskId).trim() !== ""
    ) {
      lastKnownSubTaskIdRef.current = String(watchedSubTaskId).trim();
    }
  }, [watchedSubTaskId]);

  useEffect(() => {
    if (!isEditMode) {
      setEditRecordStatusFallback("");
      setEditRecordSubTaskFallback("");
    }
  }, [isEditMode]);

  const descriptionFieldRules = useMemo(
    () => [
      {
        validator: (_: unknown, value: unknown) => {
          const html = value === null ? "" : String(value);
          const chars = countCharsFromHtml(html);
          if (chars === 0) {
            return Promise.reject(new Error("Description is required"));
          }
          if (chars < DESCRIPTION_MIN_CHARS) {
            return Promise.reject(
              new Error(
                `Description must be at least ${DESCRIPTION_MIN_CHARS} characters.`
              )
            );
          }
          if (chars > DESCRIPTION_MAX_CHARS) {
            return Promise.reject(
              new Error(
                `Description must be at most ${DESCRIPTION_MAX_CHARS} characters.`
              )
            );
          }
          return Promise.resolve();
        },
      },
    ],
    []
  );

  const isOtherTaskSelected = useMemo(() => {
    if (!watchedTaskId || !taskOptions?.length) {
      return false;
    }
    const t = taskOptions.find(x => String(x.value) === String(watchedTaskId));
    return t?.isOther === true;
  }, [watchedTaskId, taskOptions]);

  // Sub-tasks for selected parent task (skipped for "Other" task).
  // When spent-hours status is Completed, pass selected sub-task id as `sub_task` (not for WIP / other statuses).
  /**
   * Resolve `sub_task` for GET /task-subtask at fetch time (reads live form values
   * so refetch after choosing Completed never uses a stale closure without the user-selected sub-task).
   */
  const resolveTaskSubtaskApiSubTaskId = useCallback((): string => {
    const statusRaw = form.getFieldValue("status");
    const subRaw = form.getFieldValue("sub_task");
    const effStatus =
      statusRaw !== undefined &&
      statusRaw !== null &&
      String(statusRaw).trim() !== ""
        ? String(statusRaw).trim()
        : editRecordStatusFallback;
    if (!isCompletedStatusValue(effStatus, spentHoursStatusOptions)) {
      return "";
    }
    if (
      subRaw !== undefined &&
      subRaw !== null &&
      String(subRaw).trim() !== ""
    ) {
      return String(subRaw).trim();
    }
    if (editRecordSubTaskFallback) {
      return editRecordSubTaskFallback;
    }
    const refId = lastKnownSubTaskIdRef.current;
    if (refId !== undefined && refId !== null && String(refId).trim() !== "") {
      return String(refId).trim();
    }
    return "";
  }, [
    form,
    editRecordStatusFallback,
    editRecordSubTaskFallback,
    spentHoursStatusOptions,
  ]);

  /** Only refetch /task-subtask when user selects Completed (not on every status change). */
  const prevStatusForSubtaskRef = useRef<unknown>(undefined);
  useEffect(() => {
    if (
      !watchedTaskId ||
      isOtherTaskSelected ||
      !employeeId ||
      !selectedProject
    ) {
      prevStatusForSubtaskRef.current = watchedStatus;
      return;
    }
    const nowCompleted = isCompletedStatusValue(
      watchedStatus,
      spentHoursStatusOptions
    );
    const prevCompleted = isCompletedStatusValue(
      prevStatusForSubtaskRef.current,
      spentHoursStatusOptions
    );
    if (nowCompleted && !prevCompleted) {
      // Defer so Ant Design Form has flushed `sub_task` before /task-subtask reads getFieldValue.
      queueMicrotask(() => {
        void queryClient.invalidateQueries({
          queryKey: [
            "task-subtask",
            "employee-dashboard",
            String(watchedTaskId),
          ],
        });
      });
    }
    prevStatusForSubtaskRef.current = watchedStatus;
  }, [
    watchedStatus,
    watchedTaskId,
    isOtherTaskSelected,
    employeeId,
    selectedProject,
    spentHoursStatusOptions,
    queryClient,
  ]);

  /** While status is Completed, refetch when user picks a different sub-task so `/task-subtask` gets the selected `sub_task` id. */
  const prevCompletedUserSubTaskRef = useRef<string>("");
  useEffect(() => {
    if (
      !watchedTaskId ||
      isOtherTaskSelected ||
      !employeeId ||
      !selectedProject
    ) {
      return;
    }
    if (!isCompletedStatusValue(watchedStatus, spentHoursStatusOptions)) {
      prevCompletedUserSubTaskRef.current = "";
      return;
    }
    const userSid =
      watchedSubTaskId !== undefined &&
      watchedSubTaskId !== null &&
      String(watchedSubTaskId).trim() !== ""
        ? String(watchedSubTaskId).trim()
        : "";
    if (!userSid) {
      return;
    }
    if (prevCompletedUserSubTaskRef.current !== userSid) {
      prevCompletedUserSubTaskRef.current = userSid;
      queueMicrotask(() => {
        void queryClient.invalidateQueries({
          queryKey: [
            "task-subtask",
            "employee-dashboard",
            String(watchedTaskId),
          ],
        });
      });
    }
  }, [
    watchedSubTaskId,
    watchedStatus,
    watchedTaskId,
    isOtherTaskSelected,
    employeeId,
    selectedProject,
    spentHoursStatusOptions,
    queryClient,
  ]);

  const subtaskQuery = useQuery({
    queryKey: ["task-subtask", "employee-dashboard", watchedTaskId, employeeId],
    queryFn: async (): Promise<SubtaskOption[]> => {
      const taskId = String(watchedTaskId);
      const subTaskId = resolveTaskSubtaskApiSubTaskId();
      const subTaskParam = subTaskId !== "" ? { sub_task: subTaskId } : {};
      const response = await get<any>(
        "/pts_app/project-summary/task-subtask/",
        {
          params: {
            task_id: taskId,
            ...(employeeId ? { emp_id: String(employeeId) } : {}),
            ...subTaskParam,
          },
        }
      );
      const listing = response?.subtask_listing;
      if (!Array.isArray(listing)) {
        return [];
      }
      return listing.map((st: any) => {
        const rawEst = st.estimatedhours ?? st.estimated_hours ?? null;
        const parsed =
          rawEst === null || rawEst === undefined || rawEst === ""
            ? null
            : Number(rawEst);
        const emplist = Array.isArray(st.emplist) ? st.emplist : [];
        const assignedEmployeeIds = emplist
          .map((emp: any) => emp?.member_id ?? emp?.member ?? null)
          .filter(
            (id: unknown) =>
              id !== null && id !== undefined && String(id).trim() !== ""
          )
          .map((id: unknown) => String(id));
        const rawWh = st.worked_hours ?? st.workedHours ?? null;
        const worked_hours =
          rawWh === undefined || rawWh === null || String(rawWh).trim() === ""
            ? null
            : String(rawWh).trim();
        const rawUid = st.servicedesk_uid ?? st.servicedeskUid ?? null;
        const servicedesk_uid =
          rawUid === undefined ||
          rawUid === null ||
          String(rawUid).trim() === ""
            ? null
            : String(rawUid).trim();
        return {
          value: String(st.id),
          label: st.taskname ?? String(st.id),
          estimatedhours:
            parsed !== null && !Number.isNaN(parsed) ? parsed : null,
          assignedEmployeeIds,
          worked_hours,
          servicedesk_uid,
        };
      });
    },
    enabled:
      !!watchedTaskId &&
      !isOtherTaskSelected &&
      !!selectedProject &&
      !!employeeId,
    placeholderData: keepPreviousData,
  }) as UseQueryResult<SubtaskOption[], Error>;

  const subtaskOptions = subtaskQuery.data;
  const subtasksPending = subtaskQuery.isPending;
  const subtasksFetching = subtaskQuery.isFetching;

  /** True only while waiting for the first list (no rows to show yet). Avoid hiding the row on key change refetch (e.g. Completed). */
  const subtasksInitialLoading = useMemo(
    () => subtasksPending && (subtaskOptions?.length ?? 0) === 0,
    [subtasksPending, subtaskOptions]
  );

  /** Only show sub-task select when API returned at least one row in subtask_listing */
  const showSubtaskDropdown = useMemo(
    () =>
      !!watchedTaskId &&
      !isOtherTaskSelected &&
      !subtasksInitialLoading &&
      (subtaskOptions?.length ?? 0) > 0,
    [
      watchedTaskId,
      isOtherTaskSelected,
      subtasksInitialLoading,
      subtaskOptions?.length,
    ]
  );

  /** Task selected, not "Other", sub-task API finished, and no sub-tasks → show status + add sub-task entry point */
  const showStatusWithoutSubtasks = useMemo(
    () =>
      !!watchedTaskId &&
      !isOtherTaskSelected &&
      !subtasksInitialLoading &&
      (subtaskOptions?.length ?? 0) === 0,
    [
      watchedTaskId,
      isOtherTaskSelected,
      subtasksInitialLoading,
      subtaskOptions?.length,
    ]
  );

  /** Sub-task area empty while API is in flight (e.g. status Completed → refetch); Status must stay mounted separately. */
  const showSubtaskRowGapLoading = useMemo(
    () =>
      !!watchedTaskId &&
      !isOtherTaskSelected &&
      !showSubtaskDropdown &&
      !showStatusWithoutSubtasks &&
      (subtasksInitialLoading || subtasksFetching),
    [
      watchedTaskId,
      isOtherTaskSelected,
      showSubtaskDropdown,
      showStatusWithoutSubtasks,
      subtasksInitialLoading,
      subtasksFetching,
    ]
  );

  const selectedTaskLabel = useMemo(() => {
    if (!watchedTaskId || !taskOptions?.length) {
      return "";
    }
    return (
      taskOptions.find(x => String(x.value) === String(watchedTaskId))?.label ??
      ""
    );
  }, [watchedTaskId, taskOptions]);

  const selectedParentTaskWorkedHours = useMemo(() => {
    if (!watchedTaskId || !taskOptions?.length) {
      return null;
    }
    const wh = taskOptions.find(
      x => String(x.value) === String(watchedTaskId)
    )?.worked_hours;
    if (wh === undefined || wh === null || String(wh).trim() === "") {
      return null;
    }
    return String(wh).trim();
  }, [watchedTaskId, taskOptions]);

  /** Service desk ID (e.g. "TXT-10448") of the currently selected sub-task. */
  const selectedSubtaskServicedeskUid = useMemo(() => {
    if (
      watchedSubTaskId === undefined ||
      watchedSubTaskId === null ||
      String(watchedSubTaskId).trim() === "" ||
      !subtaskOptions?.length
    ) {
      return null;
    }
    const uid = subtaskOptions.find(
      o => String(o.value) === String(watchedSubTaskId)
    )?.servicedesk_uid;
    if (uid === undefined || uid === null || String(uid).trim() === "") {
      return null;
    }
    return String(uid).trim();
  }, [watchedSubTaskId, subtaskOptions]);

  const watchedHoursField = Form.useWatch("hours", form);
  const watchedMinutesField = Form.useWatch("minutes", form);

  const workedHoursFromApis = useMemo(
    () =>
      resolveDisplayedWorkedHoursFromApis(
        watchedTaskId,
        watchedSubTaskId,
        taskOptions,
        subtaskOptions,
        subtasksInitialLoading
      ),
    [
      watchedTaskId,
      watchedSubTaskId,
      taskOptions,
      subtaskOptions,
      subtasksInitialLoading,
    ]
  );

  const workedHoursEntryLabel = useMemo(
    () => formatEntryHoursMinutesLabel(watchedHoursField, watchedMinutesField),
    [watchedHoursField, watchedMinutesField]
  );

  /** Read-only Worked hours: API aggregate (H:MM) when available; else this entry’s Hours/Min as H:MM. */
  const displayedWorkedHoursLabel = useMemo(() => {
    if (isOtherTaskSelected) {
      return workedHoursEntryLabel;
    }
    if (workedHoursFromApis !== null) {
      return workedHoursFromApis;
    }
    return workedHoursEntryLabel;
  }, [isOtherTaskSelected, workedHoursFromApis, workedHoursEntryLabel]);

  const displayedEstimatedHours = useMemo(
    () =>
      resolveDisplayedEstimatedHours(
        watchedTaskId,
        watchedSubTaskId,
        taskOptions,
        subtaskOptions,
        subtasksInitialLoading
      ),
    [
      watchedTaskId,
      watchedSubTaskId,
      taskOptions,
      subtaskOptions,
      subtasksInitialLoading,
    ]
  );

  // Fetch record data for editing
  const { data: recordData, isLoading: recordLoading } = useQuery({
    queryKey: ["spent-hours-record", SPENT_HOURS_SCHEMA_NAME, editingRecordId],
    queryFn: () => {
      if (!editingRecordId) {
        throw new Error("Record ID is required");
      }
      return DynamicModuleService.getItemById(
        SPENT_HOURS_SCHEMA_NAME,
        editingRecordId
      );
    },
    enabled: !!editingRecordId && isEditMode,
  });

  // Listen for edit event from ActivityLogTab
  useEffect(() => {
    const handleEditEvent = (event: CustomEvent) => {
      const { id } = event.detail;
      if (id) {
        setEditingRecordId(id);
        setIsEditMode(true);
        setHasPrefilledFields(false);
        form.resetFields();
        form.setFieldsValue({ date: dayjs(), hours: "00", minutes: "00" });
        setSelectedProject(null);

        setTimeout(() => {
          const formElement = document.querySelector(
            "[data-spent-hours-form]"
          ) as HTMLElement;
          if (formElement) {
            const rect = formElement.getBoundingClientRect();
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop;
            const offset = 80;
            const targetPosition = rect.top + scrollTop - offset;

            window.scrollTo({
              top: Math.max(0, targetPosition),
              behavior: "smooth",
            });

            formElement.focus();
          }
        }, 150);
      }
    };

    window.addEventListener("editSpentHours", handleEditEvent as EventListener);
    return () => {
      window.removeEventListener(
        "editSpentHours",
        handleEditEvent as EventListener
      );
    };
  }, [form]);

  // Listen for add event with pre-filled fields from ActivityLogTab
  useEffect(() => {
    const handleAddEvent = (event: CustomEvent) => {
      const { project, task, category } = event.detail;

      setIsEditMode(false);
      setEditingRecordId(null);
      setHasPrefilledFields(true);

      form.setFieldsValue({
        spentproject: project ?? undefined,
        spenttask: task ?? undefined,
        category: category ?? undefined,
        sub_task: undefined,
        status: undefined,
        date: dayjs(),
        hours: "00",
        minutes: "00",
        description: undefined,
      });
      if (project) {
        setSelectedProject(project);
      }

      setTimeout(() => {
        const formElement = document.querySelector(
          "[data-spent-hours-form]"
        ) as HTMLElement;
        if (formElement) {
          const rect = formElement.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const offset = 80;
          const targetPosition = rect.top + scrollTop - offset;

          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: "smooth",
          });
        }
      }, 100);
    };

    window.addEventListener(
      "addSpentHoursWithFields",
      handleAddEvent as EventListener
    );
    return () => {
      window.removeEventListener(
        "addSpentHoursWithFields",
        handleAddEvent as EventListener
      );
    };
  }, [form]);

  // Populate form when record data is loaded for editing
  useEffect(() => {
    if (recordData && isEditMode) {
      const data = removeMetaFields(recordData);

      const dateValue =
        data[fieldNames.date] ?? data.date ?? data.work_date ?? data.entry_date;

      // Prefer discrete `hour` + `minutetime` from API (edit) so estimatedhours never fills Hours
      const minutesValue = data.minutetime ?? data.minutes ?? null;
      const hasDiscreteHour =
        data.hour !== undefined &&
        data.hour !== null &&
        String(data.hour).trim() !== "";

      let parsedHours: string = "00";
      let parsedMinutes: string = "00";

      if (hasDiscreteHour) {
        const h = Number(data.hour);
        if (!isNaN(h) && h >= 0) {
          parsedHours = String(Math.min(23, Math.floor(h))).padStart(2, "0");
        }
        if (minutesValue !== null && minutesValue !== undefined) {
          const minutes = Number(minutesValue);
          if (!isNaN(minutes) && minutes >= 0 && minutes < 60) {
            const roundedMinutes = Math.round(minutes / 5) * 5;
            const finalMinutes = roundedMinutes >= 60 ? 55 : roundedMinutes;
            parsedMinutes = String(finalMinutes).padStart(2, "0");
          }
        }
      } else {
        // Parse decimal hours field (e.g., 8.5) when hour/minutetime not provided
        const hoursValue =
          data[fieldNames.hours] ?? data.hours ?? data.hour ?? null;

        if (minutesValue !== null && minutesValue !== undefined) {
          const minutes = Number(minutesValue);
          if (!isNaN(minutes) && minutes >= 0 && minutes < 60) {
            const roundedMinutes = Math.round(minutes / 5) * 5;
            const finalMinutes = roundedMinutes >= 60 ? 55 : roundedMinutes;
            parsedMinutes = String(finalMinutes).padStart(2, "0");
          }
        }

        if (hoursValue !== null && hoursValue !== undefined) {
          const totalHours = Number(hoursValue);
          if (!isNaN(totalHours) && totalHours >= 0) {
            const hours = Math.floor(totalHours);
            parsedHours = String(hours).padStart(2, "0");

            if (minutesValue === null || minutesValue === undefined) {
              const decimalPart = totalHours - hours;
              const minutes = Math.round(decimalPart * 60);
              const roundedMinutes = Math.round(minutes / 5) * 5;
              const finalMinutes =
                roundedMinutes >= 60 ? 55 : Math.max(0, roundedMinutes);
              parsedMinutes = String(finalMinutes).padStart(2, "0");
            }
          }
        }
      }

      const newValues: any = {
        spentproject: data[fieldNames.project] ?? data.spentproject ?? null,
        spenttask: data[fieldNames.task] ?? data.spenttask ?? null,
        category: data[fieldNames.category] ?? data.category ?? null,
        sub_task:
          data[fieldNames.subTask] ??
          data.sub_task_id ??
          data.subtask_id ??
          data.spent_subtask_id ??
          data.spent_subtask ??
          data.sub_task ??
          data.other_task ??
          null,
        status:
          data[fieldNames.status] ??
          data.status ??
          data.approval_status ??
          null,
        date: dateValue ? dayjs(dateValue) : dayjs(),
        hours: parsedHours,
        minutes: parsedMinutes,
        description: data[fieldNames.description] ?? data.description ?? null,
      };

      setEditRecordStatusFallback(
        newValues.status === undefined || newValues.status === null
          ? ""
          : String(newValues.status).trim()
      );
      setEditRecordSubTaskFallback(
        newValues.sub_task === undefined || newValues.sub_task === null
          ? ""
          : String(newValues.sub_task).trim()
      );

      form.setFieldsValue(newValues);
      if (newValues.spentproject) {
        setSelectedProject(String(newValues.spentproject));
      }
    }
  }, [recordData, isEditMode, fieldNames, form]);

  // Handle project change
  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    form.setFieldsValue({
      spentproject: value,
      spenttask: undefined, // Clear task when project changes
      sub_task: undefined,
      status: undefined,
    });
  };

  const handleTaskChange = () => {
    form.setFieldsValue({
      sub_task: undefined,
      status: undefined,
    });
  };

  // Reset form
  const resetForm = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(),
      hours: "00",
      minutes: "00",
    });
    setIsEditMode(false);
    setEditingRecordId(null);
    setHasPrefilledFields(false);
    setSelectedProject(null);
  }, [form]);

  const handleCancelEdit = () => {
    resetForm();
  };

  // Check previous hours validation (pass spent_hours_id when updating so API excludes current record)
  const checkPreviousHoursValidation = async (
    date: any,
    hours: number,
    minutes: number,
    spentHoursId?: string
  ): Promise<boolean> => {
    if (!employeeId) {
      return true;
    }

    try {
      // Format date to YYYY-MM-DD
      let formattedDate = "";
      if (date) {
        if (dayjs.isDayjs(date)) {
          formattedDate = date.format("YYYY-MM-DD");
        } else if (date instanceof Date) {
          formattedDate = dayjs(date).format("YYYY-MM-DD");
        } else if (typeof date === "string") {
          // If date is in DD-MM-YYYY format, convert to YYYY-MM-DD
          const parsedDate = dayjs(date, "DD-MM-YYYY");
          if (parsedDate.isValid()) {
            formattedDate = parsedDate.format("YYYY-MM-DD");
          } else {
            // Try parsing as is
            formattedDate = dayjs(date).format("YYYY-MM-DD");
          }
        }
      }

      const response = await PtsSpentHoursService.checkPreviousHoursValidation({
        employee_id: employeeId,
        date: formattedDate,
        hours: hours,
        minutes: minutes,
        ...(spentHoursId ? { spent_hours_id: spentHoursId } : {}),
      });

      if (response?.is_valid === false) {
        setValidationMessage(
          response?.message ??
            "Hours validation failed. Please check your hours entry."
        );
        setValidationModalVisible(true);
        return false;
      }

      return true;
    } catch (error: any) {
      console.error("Error checking previous hours validation:", error);
      notify.error(
        error?.message ??
          "Failed to validate hours. Proceeding with submission."
      );
      return true;
    }
  };

  const invalidateSpentHoursQueries = useCallback(
    (taskId: unknown) => {
      queryClient.invalidateQueries({
        queryKey: ["spent-hours-data", SPENT_HOURS_SCHEMA_NAME],
      });
      queryClient.invalidateQueries({
        queryKey: ["moduleData", SPENT_HOURS_SCHEMA_NAME],
      });
      if (
        taskId !== undefined &&
        taskId !== null &&
        String(taskId).trim() !== ""
      ) {
        void queryClient.invalidateQueries({
          queryKey: ["task-subtask", "activity-log-tab", String(taskId)],
        });
        void queryClient.invalidateQueries({
          queryKey: ["task-subtask", "employee-dashboard", String(taskId)],
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ["employee-dashboard", employeeId],
      });
    },
    [queryClient, employeeId]
  );

  const executeSpentHoursSubmit = useCallback(
    async (
      values: Record<string, unknown>,
      payload: Record<string, unknown>
    ): Promise<boolean> => {
      const taskId = values.spenttask;

      if (isEditMode && editingRecordId) {
        const response = await DynamicModuleService.updateItem(
          SPENT_HOURS_SCHEMA_NAME,
          editingRecordId,
          payload
        );

        if (response) {
          resetForm();
          invalidateSpentHoursQueries(taskId);
          return true;
        }
        return false;
      }

      const response = await DynamicModuleService.createItem(
        SPENT_HOURS_SCHEMA_NAME,
        payload
      );

      if (response) {
        resetForm();
        invalidateSpentHoursQueries(taskId);
        return true;
      }
      return false;
    },
    [editingRecordId, invalidateSpentHoursQueries, isEditMode, resetForm]
  );

  const closeRemainingTimeModal = useCallback(() => {
    setRemainingTimeModalVisible(false);
    setRemainingTimeReason("");
    setRemainingTimeReasonError(null);
    setPendingRemainingTimeSubmit(null);
  }, []);

  const handleRemainingTimeReasonChange = (
    e: ChangeEvent<HTMLTextAreaElement>
  ) => {
    const nextValue = e.target.value;
    if (nextValue.length <= REMAINING_TIME_REASON_MAX_CHARS) {
      setRemainingTimeReason(nextValue);
      setRemainingTimeReasonError(validateRemainingTimeReason(nextValue));
    }
  };

  const handleRemainingTimeModalSubmit = async () => {
    if (!pendingRemainingTimeSubmit || !employeeId) {
      return;
    }

    const reasonError = validateRemainingTimeReason(remainingTimeReason);
    if (reasonError) {
      setRemainingTimeReasonError(reasonError);
      return;
    }

    const trimmedReason = remainingTimeReason.trim();

    setIsSubmitting(true);
    try {
      const spentHoursSuccess = await executeSpentHoursSubmit(
        pendingRemainingTimeSubmit.values,
        pendingRemainingTimeSubmit.payload
      );

      if (!spentHoursSuccess) {
        notify.error("Failed to submit spent hours. Please try again.");
        return;
      }

      await PtsRemainingTimeRequestService.createRemainingTimeRequest({
        emp_id: employeeId,
        remaining_time: pendingRemainingTimeSubmit.remainingTime,
        reason: trimmedReason,
        date: pendingRemainingTimeSubmit.formattedDate,
      });

      void queryClient.invalidateQueries({
        queryKey: ["remaining-time-request"],
      });

      closeRemainingTimeModal();
    } catch (error: any) {
      console.error("Error submitting remaining time request:", error);
      notify.error(
        error?.message ??
          "Spent hours saved, but remaining time request failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (showStatusWithoutSubtasks) {
        notify.error(
          "Sub task is required. Please click 'Add sub task' and create one before submitting."
        );
        return;
      }

      // Format date field to DD-MM-YYYY
      let formattedDate = "";
      if (values.date && dayjs.isDayjs(values.date)) {
        formattedDate = values.date.format("DD-MM-YYYY");
      } else if (values.date instanceof Date) {
        formattedDate = dayjs(values.date).format("DD-MM-YYYY");
      }

      // Convert hours and minutes from form to decimal hours for database storage
      // Example: 8 hours + 30 minutes = 8.5 decimal hours
      // Handle both string and number values (form might return "05" or 5)
      // Also handle empty strings, null, and undefined values
      const hoursValue = values.hours ?? values.hour ?? "00";
      const minutesValue = values.minutes ?? values.minutetime ?? "00";
      // Convert to number - Number() handles "05" correctly (becomes 5)
      // Handle empty strings and null/undefined by defaulting to 0
      const hours =
        hoursValue === "" || hoursValue === null || hoursValue === undefined
          ? 0
          : Number(hoursValue) || 0;
      const minutes =
        minutesValue === "" ||
        minutesValue === null ||
        minutesValue === undefined
          ? 0
          : Number(minutesValue) || 0;
      // Calculate total decimal hours: hours + (minutes / 60)
      // Round to 2 decimal places to avoid floating point precision issues
      const totalHours = Math.round((hours + minutes / 60) * 100) / 100;

      // Format hours and minutes as strings with leading zeros (e.g., "05", "30")
      const hourString = String(hours).padStart(2, "0");
      const minuteString = String(minutes).padStart(2, "0");

      // Use correct field names from schema
      const payload: any = {};
      payload[fieldNames.project] = values.spentproject;
      payload[fieldNames.task] = values.spenttask;
      payload[fieldNames.category] = values.category;

      if (values.status) {
        payload[fieldNames.status] = values.status;
      }

      // Sub-task API id and "Other" free text both use the same schema field `sub_task`
      const subVal = values.sub_task;
      if (
        subVal !== undefined &&
        subVal !== null &&
        String(subVal).trim() !== ""
      ) {
        payload[fieldNames.subTask] = String(subVal).trim();
      } else {
        payload[fieldNames.subTask] = null;
      }
      payload[fieldNames.date] = formattedDate;
      // Pass both decimal hours (for schema field) and separate hour/minutetime fields
      payload[fieldNames.hours] = totalHours;
      // Add hour and minutetime fields for API compatibility
      payload.hour = hourString;
      payload.minutetime = minuteString;
      const normalizedDescription = trimQuillContent(values.description ?? "");
      payload[fieldNames.description] = normalizedDescription || null;

      const selectedTaskMeta = taskOptions?.find(
        t => String(t.value) === String(values.spenttask)
      );
      if (selectedTaskMeta?.isOther) {
        payload[fieldNames.estimatedHours] = null;
      } else {
        const resolvedEstimatedHours = resolveDisplayedEstimatedHours(
          values.spenttask,
          values.sub_task,
          taskOptions,
          subtaskOptions,
          subtasksInitialLoading
        );
        if (
          resolvedEstimatedHours !== null &&
          resolvedEstimatedHours !== undefined &&
          !Number.isNaN(resolvedEstimatedHours)
        ) {
          payload[fieldNames.estimatedHours] = resolvedEstimatedHours;
        }
      }

      // Keep work_hours aligned with the value shown in UI/API format (H:MM),
      // instead of sending decimal hours.
      payload.work_hours =
        displayedWorkedHoursLabel &&
        String(displayedWorkedHoursLabel).trim() !== ""
          ? String(displayedWorkedHoursLabel).trim()
          : `${hourString}:${minuteString}`;

      // Add employee ID
      if (employeeId) {
        payload.emp_id = employeeId;
      }

      // Only check validation for new entries
      // if (!isEditMode) {
      //   setIsSubmitting(true);
      //   const isValid = await checkPreviousHoursValidation(
      //     values.date,
      //     hours,
      //     minutes
      //   );
      //   if (!isValid) {
      //     setIsSubmitting(false);
      //     return;
      //   }
      // } else {
      //   setIsSubmitting(true);
      // }

      setIsSubmitting(true);

      // Always validate; when updating, pass spent_hours_id so API excludes current record
      const isValid = await checkPreviousHoursValidation(
        values.date,
        hours,
        minutes,
        isEditMode && editingRecordId ? editingRecordId : undefined
      );

      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      const formDateIsToday =
        !!values.date && dayjs(values.date).isSame(dayjs(), "day");
      const formDate = values.date
        ? dayjs.isDayjs(values.date)
          ? values.date
          : dayjs(values.date)
        : null;
      const isTodayWeekend =
        formDateIsToday && formDate !== null && isWeekend(formDate);

      let productiveMinutes = parseProductiveHoursToMinutes(
        dashboardData?.today_hours?.productive_hours
      );

      if (formDateIsToday && employeeId) {
        try {
          const freshDashboard =
            await PtsEmployeeService.getEmployeeDashboard(employeeId);
          productiveMinutes = parseProductiveHoursToMinutes(
            freshDashboard?.today_hours?.productive_hours
          );
        } catch {
          // Keep cached dashboard productive hours if refresh fails.
        }
      }

      const remainingTimeResult =
        calculateRemainingTimeFromProductiveHours(productiveMinutes);

      let todaySpentHoursCount = 0;
      if (formDateIsToday && employeeId && formDate) {
        try {
          todaySpentHoursCount = await getTodaySpentHoursCount(
            employeeId,
            formDate
          );
        } catch {
          todaySpentHoursCount = -1;
        }
      }

      const shouldShowRemainingTimeModal =
        !isEditMode &&
        formDateIsToday &&
        !isTodayWeekend &&
        remainingTimeResult !== null &&
        todaySpentHoursCount === 0 &&
        remainingTimeResult.remainingMinutes >
          REMAINING_TIME_MODAL_THRESHOLD_MINUTES;

      if (shouldShowRemainingTimeModal && remainingTimeResult) {
        setPendingRemainingTimeSubmit({
          values,
          payload,
          formattedDate,
          remainingTime: remainingTimeResult.payload,
          remainingTimeDisplay: remainingTimeResult.display,
          productiveTimeDisplay: remainingTimeResult.productiveDisplay,
        });
        setRemainingTimeReason("");
        setRemainingTimeReasonError(null);
        setRemainingTimeModalVisible(true);
        setIsSubmitting(false);
        return;
      }

      const spentHoursSuccess = await executeSpentHoursSubmit(values, payload);
      if (!spentHoursSuccess) {
        notify.error(
          `Failed to ${isEditMode ? "update" : "add"} spent hours. Please try again.`
        );
      }
    } catch (error: any) {
      console.error("Error submitting spent hours:", error);
      notify.error(
        error?.message ??
          `Failed to ${isEditMode ? "update" : "add"} spent hours. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (schemaLoading || (isEditMode && recordLoading)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ReactQuill Editor Styling */
        .spent-hours-quill-editor {
          width: 100% !important;
        }
        
        /* Container must not fix a tiny height while .ql-editor uses a larger min-height (breaks scroll) */
        .spent-hours-quill-editor .ql-container {
          height: auto !important;
          overflow: hidden !important;
          border-bottom-left-radius: 0.5rem !important;
          border-bottom-right-radius: 0.5rem !important;
          border: 1px solid #e5e7eb !important;
          font-size: 14px !important;
        }
        
        .spent-hours-quill-editor .ql-editor {
          min-height: 80px !important;
          //  min-height: 140px !important;
          max-height: 200px !important;
          height: auto !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          overflow-anchor: none !important;
          padding: 12px !important;
          line-height: 1.6 !important;
          -webkit-overflow-scrolling: touch !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }
        
        .spent-hours-quill-editor .ql-editor p,
        .spent-hours-quill-editor .ql-editor li {
          overflow-wrap: anywhere !important;
          word-break: break-word !important;
        }
        
        .spent-hours-quill-editor .ql-toolbar {
          border-top-left-radius: 0.5rem !important;
          border-top-right-radius: 0.5rem !important;
          border: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          background-color: #fafafa !important;
          padding: 2px !important;
        }
        
        .spent-hours-quill-editor .ql-toolbar .ql-stroke {
          stroke: #6b7280 !important;
        }
        
        .spent-hours-quill-editor .ql-toolbar .ql-fill {
          fill: #6b7280 !important;
        }
        
        .spent-hours-quill-editor .ql-toolbar button:hover,
        .spent-hours-quill-editor .ql-toolbar button.ql-active {
          color: #3b82f6 !important;
        }
        
        .spent-hours-quill-editor .ql-toolbar button:hover .ql-stroke,
        .spent-hours-quill-editor .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3b82f6 !important;
        }
        
        .spent-hours-quill-editor .ql-toolbar button:hover .ql-fill,
        .spent-hours-quill-editor .ql-toolbar button.ql-active .ql-fill {
          fill: #3b82f6 !important;
        }
        
        .spent-hours-quill-editor .ql-editor::-webkit-scrollbar {
          width: 8px;
        }
        
        .spent-hours-quill-editor .ql-editor::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .spent-hours-quill-editor .ql-editor::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        
        .spent-hours-quill-editor .ql-editor::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        .spent-hours-quill-editor .ql-container::-webkit-scrollbar {
          display: none !important;
        }
        
        .spent-hours-quill-editor .ql-container {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
        
        /* Contain scroll inside editor after paste */
        .quill-editor-scroll-wrap {
          overflow: hidden !important;
          position: relative !important;
          width: 100% !important;
          min-width: 0 !important;
        }
        
        .quill-editor-scroll-wrap .ql-editor {
          overscroll-behavior: contain !important;
        }
        
        /* Dropdown Background Styling */
        [data-radix-popper-content-wrapper] {
          background: white !important;
        }
        
        /* Select Content (Dropdown Menu) */
        [role="listbox"] {
          background: white !important;
        }
        
        /* Select Content wrapper */
        .bg-popover {
          background: white !important;
          border: 1px solid #e5e7eb !important;
        }
        
        /* Select Items */
        [data-radix-select-item] {
          background: white !important;
        }
        
        [data-radix-select-item]:hover,
        [data-radix-select-item][data-highlighted],
        [data-radix-select-item][data-state="checked"] {
          background: #f3f4f6 !important;
        }
        
        /* Ensure white background for all select dropdowns */
        [data-radix-select-content] {
          background: white !important;
        }
        
        [data-radix-select-viewport] {
          background: white !important;
        }
        
        /* Hours Warning Modal Styling */
        .hours-warning-modal .ant-modal-content {
          border-radius: 12px !important;
          overflow: hidden;
        }
        
        .hours-warning-modal .ant-modal-header {
          display: none !important;
        }
        
        .hours-warning-modal .ant-modal-body {
          padding: 24px !important;
        }
        
        .hours-warning-modal .ant-modal-close {
          top: 16px !important;
          right: 16px !important;
          color: #6b7280 !important;
        }
        
        .hours-warning-modal .ant-modal-close:hover {
          color: #374151 !important;
        }
      `}</style>
      <div
        className="scroll-mt-20 rounded-xl border bg-white transition-all duration-300"
        data-spent-hours-form
        tabIndex={-1}
        style={{ scrollMarginTop: "80px" }}
      >
        <Card className="border-0 shadow-none">
          <CardContent className="relative p-3 pb-20 sm:pb-4 md:p-6">
            {/* Header with Icon and Title */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <Clock size={16} />
                {isEditMode ? "Edit Work Hours" : "Log Work Hours"}
              </div>
            </div>

            {/* Custom Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ date: dayjs(), hours: "00", minutes: "00" }}
              className="space-y-4"
            >
              {/* Row 1: Date, Project, Task, Estimated + Worked hours (5 cols); "Other" task uses 4 cols */}
              <div
                className={`grid grid-cols-1 gap-2 md:gap-4 ${isOtherTaskSelected ? "lg:grid-cols-4" : "lg:grid-cols-5"}`}
              >
                {/* Date Field */}
                <Form.Item
                  name="date"
                  label={<span>Date:</span>}
                  rules={[{ required: true, message: "Date is required" }]}
                  className="md:col-span-1"
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    placeholder="15-01-2026"
                    className="h-[2rem] rounded-lg border-gray-200 bg-[#fafafa]"
                  />
                </Form.Item>

                {/* Project Field */}
                <Form.Item
                  name="spentproject"
                  label={<span>Select Project:</span>}
                  rules={[{ required: true, message: "Project is required" }]}
                  className="md:col-span-1"
                >
                  <Select
                    allowClear
                    placeholder="Choose a project"
                    showSearch
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={projectOptions?.map(
                      (project: { value: string; label: string }) => ({
                        value: project.value,
                        label: project.label,
                      })
                    )}
                    onChange={handleProjectChange}
                  />
                </Form.Item>

                {/* Task Field */}
                <Form.Item
                  name="spenttask"
                  label={<span>Select Tasks:</span>}
                  rules={[{ required: true, message: "Task is required" }]}
                  className="md:col-span-1"
                >
                  <Select
                    allowClear
                    placeholder={
                      tasksLoading
                        ? "Loading tasks..."
                        : !selectedProject
                          ? "Choose a project"
                          : "Choose a task"
                    }
                    showSearch
                    disabled={!selectedProject || tasksLoading}
                    loading={tasksLoading}
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={taskOptions?.map((task: ProjectTaskOption) => ({
                      value: task.value,
                      label: task.label,
                    }))}
                    onChange={handleTaskChange}
                  />
                </Form.Item>

                {watchedTaskId && isOtherTaskSelected && (
                  <div className="flex min-w-0 flex-col gap-3 md:col-span-1">
                    <Form.Item
                      name="sub_task"
                      label={<span>Sub Task Name:</span>}
                      className="mb-0"
                      validateTrigger={["onChange", "onBlur"]}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Sub Task Name is required",
                        },
                        {
                          validator: (_, value) => {
                            const raw = value === null ? "" : String(value);
                            if (raw.length > TASK_NAME_DESC_MAX_LEN) {
                              return Promise.reject(
                                new Error(
                                  `Sub Task Name must be at most ${TASK_NAME_DESC_MAX_LEN} characters.`
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Describe the work for this entry" />
                    </Form.Item>

                    <Form.Item
                      name="status"
                      label={<span>Status:</span>}
                      className="mb-0"
                      rules={[
                        { required: true, message: "Status is required" },
                      ]}
                    >
                      <Select
                        allowClear={false}
                        placeholder="Select status"
                        options={spentHoursStatusOptions}
                      />
                    </Form.Item>

                    <Form.Item
                      label={<span>Worked hours:</span>}
                      className="mb-0"
                    >
                      <Input
                        disabled
                        className="h-[32px] font-semibold text-slate-950"
                        value={displayedWorkedHoursLabel}
                        placeholder="—"
                      />
                    </Form.Item>
                  </div>
                )}

                {/* Estimated hours from task API; hidden for "Other" task */}
                {!isOtherTaskSelected && (
                  <>
                    <Form.Item
                      label={<span>Estimated hours:</span>}
                      className="min-w-0 md:col-span-1"
                    >
                      <Input
                        disabled
                        className="h-[32px] font-semibold text-slate-950"
                        value={
                          watchedTaskId && displayedEstimatedHours !== null
                            ? String(displayedEstimatedHours)
                            : ""
                        }
                        placeholder={watchedTaskId ? "—" : "0"}
                      />
                    </Form.Item>
                    <Form.Item
                      label={<span>Worked hours:</span>}
                      className="min-w-0 md:col-span-1"
                    >
                      <Input
                        disabled
                        className="h-[32px] font-semibold text-slate-950"
                        value={displayedWorkedHoursLabel}
                        placeholder="—"
                      />
                    </Form.Item>
                  </>
                )}

                {/* Category Field */}
                {/* <Form.Item
                  name="category"
                  label={<span>Category:</span>}
                  rules={[{ required: true, message: "Category is required" }]}
                  className="md:col-span-1"
                >
                  <Select
                    allowClear
                    placeholder="Select a category"
                    showSearch
                    className="task-form-select"
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={categoryOptions?.map(
                      (category: { value: string; label: string }) => ({
                        value: category.value,
                        label: category.label,
                      })
                    )}
                  />
                </Form.Item> */}
              </div>

              {/* Row 2: Sub task (+ manage beside select) + Status — Status always mounted for non-"Other" tasks so refetch gaps cannot unregister it */}
              {watchedTaskId && !isOtherTaskSelected && (
                <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 md:grid-cols-12 md:items-end">
                  <div className="mb-0 min-w-0 md:col-span-6">
                    {showSubtaskDropdown && (
                      <Form.Item
                        label={<span>Sub task:</span>}
                        required
                        className="mb-0"
                      >
                        <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                          <div className="min-w-0 flex-1">
                            <Form.Item
                              name="sub_task"
                              noStyle
                              rules={[
                                {
                                  required: true,
                                  message: "Sub task is required",
                                },
                              ]}
                            >
                              <Select
                                allowClear
                                placeholder="Choose a sub task"
                                className="task-form-select w-full max-w-full"
                                options={subtaskOptions ?? []}
                                filterOption={(input, option) => {
                                  const label = String(option?.label ?? "");
                                  const uid = String(
                                    (option as SubtaskOption)
                                      ?.servicedesk_uid ?? ""
                                  );
                                  const term = input.toLowerCase();
                                  return (
                                    label.toLowerCase().includes(term) ||
                                    uid.toLowerCase().includes(term)
                                  );
                                }}
                                optionRender={option => {
                                  const data = option.data as SubtaskOption;
                                  return (
                                    <div className="flex min-w-0 items-center gap-2">
                                      <span className="min-w-0 flex-1 truncate">
                                        {data?.label}
                                      </span>
                                      {data?.servicedesk_uid ? (
                                        <span className="shrink-0 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700">
                                          {data.servicedesk_uid}
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                }}
                                showSearch
                                loading={subtasksFetching}
                                popupMatchSelectWidth={false}
                              />
                            </Form.Item>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 w-full shrink-0 gap-1.5 sm:w-auto"
                            onClick={() => setSubtaskModalOpen(true)}
                          >
                            <Plus className="h-4 w-4 shrink-0" />
                            Add / manage sub tasks
                          </Button>
                        </div>
                      </Form.Item>
                    )}
                    {showStatusWithoutSubtasks && (
                      <div className="flex pb-2 md:justify-start md:self-end md:pb-1">
                        <div className="flex w-full flex-col gap-1.5 sm:w-auto">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 w-full gap-1.5 border-red-300 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-700 sm:w-auto"
                            onClick={() => setSubtaskModalOpen(true)}
                          >
                            <Plus className="h-4 w-4 shrink-0" />
                            Add sub task *
                          </Button>
                          <span className="text-xs text-red-600">
                            Sub task is required for this task.
                          </span>
                        </div>
                      </div>
                    )}
                    {showSubtaskRowGapLoading && (
                      <div className="flex min-h-[2.25rem] items-center text-sm text-gray-500">
                        Loading sub-tasks…
                      </div>
                    )}
                  </div>
                  {showSubtaskDropdown && (
                    <div className="mb-[10px] flex min-w-0 flex-col md:col-span-2">
                      <span className="mb-2 text-[13px] font-normal leading-[22px]">
                        Service Desk ID:
                      </span>
                      <div className="flex h-[32px] items-center">
                        {selectedSubtaskServicedeskUid ? (
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-semibold text-blue-700">
                            {selectedSubtaskServicedeskUid}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                  )}
                  <Form.Item
                    name="status"
                    label={<span>Status:</span>}
                    className="mb-0 min-w-0 md:col-span-2"
                    rules={[{ required: true, message: "Status is required" }]}
                  >
                    <Select
                      allowClear={false}
                      placeholder="Select status"
                      className="task-form-select w-full max-w-full"
                      options={spentHoursStatusOptions}
                    />
                  </Form.Item>
                </div>
              )}

              {/* Second Row: Description (left) and Hours/Minutes (right) */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                <Form.Item
                  name="description"
                  label={<span>Description:</span>}
                  required
                  validateTrigger={["onChange", "onBlur"]}
                  rules={descriptionFieldRules}
                  help=""
                  className="md:col-span-6"
                >
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue, setFieldValue }) => {
                      const value = getFieldValue("description");
                      const descriptionError =
                        form.getFieldError("description")[0] ?? "";
                      const usedChars = countCharsFromHtml(String(value ?? ""));
                      const remainingChars = DESCRIPTION_MAX_CHARS - usedChars;
                      const charsNeeded = DESCRIPTION_MIN_CHARS - usedChars;
                      const counterClass =
                        remainingChars < 0 ||
                        (usedChars > 0 && usedChars < DESCRIPTION_MIN_CHARS)
                          ? "font-medium text-red-600"
                          : remainingChars <= 10
                            ? "text-amber-600"
                            : "text-gray-500";
                      return (
                        <div>
                          <div className="quill-editor-scroll-wrap">
                            <ReactQuill
                              ref={quillEditorRef}
                              theme="snow"
                              value={value ?? ""}
                              onChange={val => {
                                setFieldValue("description", val);
                                requestAnimationFrame(() => {
                                  void form
                                    .validateFields(["description"])
                                    .catch(() => {
                                      /* invalid: min/max character limits */
                                    });
                                  const quill =
                                    quillEditorRef.current?.getEditor?.();
                                  if (!quill?.root) {
                                    return;
                                  }
                                  const sel = quill.getSelection(true);
                                  if (sel !== null) {
                                    try {
                                      const bounds = quill.getBounds(
                                        sel.index,
                                        sel.length || 1
                                      );
                                      const root = quill.root;
                                      const scrollBottom =
                                        root.scrollTop + root.clientHeight;
                                      const boundsBottom =
                                        bounds.top + bounds.height;
                                      if (boundsBottom > scrollBottom) {
                                        root.scrollTop =
                                          boundsBottom - root.clientHeight;
                                      }
                                      if (bounds.top < root.scrollTop) {
                                        root.scrollTop = bounds.top;
                                      }
                                    } catch {
                                      /* getBounds can throw in edge cases */
                                    }
                                  }
                                });
                              }}
                              className="spent-hours-quill-editor"
                              modules={{
                                toolbar: [
                                  [{ header: [1, 2, 3, false] }],
                                  ["bold", "italic", "underline", "strike"],
                                  [{ list: "ordered" }, { list: "bullet" }],
                                  [{ indent: "-1" }, { indent: "+1" }],
                                  ["link"],
                                  ["clean"],
                                ],
                              }}
                              placeholder="What did you work on?"
                            />
                          </div>
                          <div
                            className="mt-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs"
                            aria-live="polite"
                          >
                            <span className="text-sm text-red-500">
                              {descriptionError}
                            </span>
                            <div
                              className={`flex items-center gap-x-2 ${counterClass} mr-1`}
                            >
                              <span>
                                {remainingChars < 0
                                  ? `${Math.abs(remainingChars)} over limit`
                                  : usedChars > 0 && charsNeeded > 0
                                    ? `${charsNeeded} more character${charsNeeded === 1 ? "" : "s"} needed`
                                    : `${remainingChars} character${remainingChars === 1 ? "" : "s"} remaining`}
                              </span>
                              <span
                                className={
                                  remainingChars < 0 ||
                                  (usedChars > 0 &&
                                    usedChars < DESCRIPTION_MIN_CHARS)
                                    ? "text-red-500"
                                    : "text-gray-400"
                                }
                              >
                                ({usedChars}/{DESCRIPTION_MAX_CHARS})
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </Form.Item>
                </Form.Item>

                {/* Hours and Minutes - Right side, side-by-side */}
                <div
                  className={
                    isCompletedStatusValue(
                      watchedStatus,
                      spentHoursStatusOptions
                    )
                      ? "md:col-span-6"
                      : "md:col-span-4"
                  }
                >
                  <div className="grid gap-2 md:grid-cols-2 md:gap-4">
                    {/* Hours Field */}
                    <Form.Item
                      name="hours"
                      label={<span>Hours:</span>}
                      rules={[
                        { required: true, message: "Hours is required" },
                        {
                          validator: (_, value) => {
                            const minutes = form.getFieldValue("minutes");
                            if (
                              (!value || value === "00") &&
                              (!minutes || minutes === "00")
                            ) {
                              return Promise.reject(
                                new Error(
                                  "Please enter at least some hours or minutes"
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        placeholder="00 Hrs."
                        showSearch
                        className="task-form-select"
                        options={hoursOptions}
                      />
                    </Form.Item>

                    {/* Minutes Field */}
                    <Form.Item
                      name="minutes"
                      label={<span>Minutes:</span>}
                      rules={[
                        { required: true, message: "Minutes is required" },
                        {
                          validator: (_, value) => {
                            const hours = form.getFieldValue("hours");
                            if (
                              (!value || value === "00") &&
                              (!hours || hours === "00")
                            ) {
                              return Promise.reject(
                                new Error(
                                  "Please enter at least some hours or minutes"
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        placeholder="00 Min."
                        showSearch
                        className="task-form-select"
                        options={minutesOptions}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* Submit Button - Positioned at bottom right */}
              <div className="absolute bottom-[20px] right-[25px] flex justify-end gap-2 sm:bottom-[25px]">
                {(isEditMode || hasPrefilledFields) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (!!watchedTaskId &&
                      !isOtherTaskSelected &&
                      subtasksInitialLoading) ||
                    showStatusWithoutSubtasks
                  }
                  className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                >
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Submitting..."
                    : isEditMode
                      ? "Update"
                      : "Submit"}
                </Button>
              </div>
            </Form>
          </CardContent>
        </Card>

        {/* Validation Error Modal */}
        {selectedProject && watchedTaskId && !isOtherTaskSelected && (
          <SpentHoursSubtaskModal
            open={subtaskModalOpen}
            onClose={() => setSubtaskModalOpen(false)}
            projectId={selectedProject}
            parentTaskId={String(watchedTaskId)}
            parentTaskLabel={selectedTaskLabel || "Task"}
            parentTaskWorkedHours={selectedParentTaskWorkedHours}
            subtasks={subtaskOptions ?? []}
            defaultEmployeeId={employeeId}
            onSuccess={() => {
              /* Modal keeps focus; sub-task query refreshes via invalidate */
            }}
          />
        )}

        <Modal
          title={null}
          open={validationModalVisible}
          onCancel={() => setValidationModalVisible(false)}
          closable
          maskClosable
          width={450}
          footer={null}
          className="hours-warning-modal"
        >
          <div className="">
            <div className="flex flex-col items-center text-center">
              {/* Warning Icon with Clock */}
              <div className="relative mb-4">
                <div className="flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full bg-amber-100">
                  <div className="relative">
                    <Clock className="h-10 w-10 text-amber-600" />
                  </div>
                </div>
              </div>

              {/* Title */}
              <h3 className="mb-3 text-xl font-semibold text-gray-900">
                Hours Warning
              </h3>

              {/* Message */}
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center">
                <p className="text-sm leading-relaxed text-gray-700">
                  {validationMessage}
                </p>
              </div>

              {/* Action Button */}
              <Button
                onClick={() => setValidationModalVisible(false)}
                className="bg-primary hover:bg-primary/90 w-full max-w-[15rem] text-white"
              >
                I Understand
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          title={null}
          open={remainingTimeModalVisible}
          onCancel={() => {
            if (!isSubmitting) {
              closeRemainingTimeModal();
            }
          }}
          closable={!isSubmitting}
          maskClosable={!isSubmitting}
          width={480}
          footer={null}
          destroyOnClose
          className="remaining-time-request-modal"
        >
          <div className="space-y-5 pt-1">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full bg-orange-100">
                <Timer className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                Remaining Time Request
              </h3>
            </div>

            <p className="text-center text-sm leading-relaxed text-gray-600">
              You still have{" "}
              <span className="font-semibold text-orange-600">
                {pendingRemainingTimeSubmit?.remainingTimeDisplay ?? "--"}
              </span>{" "}
              hours left to complete{" "}
              <span className="font-semibold text-gray-900">
                {TOTAL_WORKING_HOURS_LABEL}
              </span>{" "}
              working hours. Please provide a reason.
            </p>

            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                Remaining Time
              </p>
              <p className="mt-1 text-3xl font-bold text-orange-600">
                {pendingRemainingTimeSubmit?.remainingTimeDisplay ?? "--"}
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="remaining-time-reason"
                className="text-sm font-medium text-gray-900"
              >
                Reason <span className="text-red-500">*</span>
              </label>
              <Input.TextArea
                id="remaining-time-reason"
                value={remainingTimeReason}
                onChange={handleRemainingTimeReasonChange}
                placeholder="Enter reason for remaining time"
                rows={4}
                maxLength={REMAINING_TIME_REASON_MAX_CHARS}
                disabled={isSubmitting}
                status={remainingTimeReasonError ? "error" : undefined}
                className="rounded-md"
              />
              {remainingTimeReasonError ? (
                <p className="text-xs text-red-500">
                  {remainingTimeReasonError}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  {remainingTimeReason.trim().length}/
                  {REMAINING_TIME_REASON_MAX_CHARS} characters
                  {/* (minimum
                  {REMAINING_TIME_REASON_MIN_CHARS}) */}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeRemainingTimeModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleRemainingTimeModalSubmit}
                disabled={
                  isSubmitting ||
                  !!validateRemainingTimeReason(remainingTimeReason)
                }
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default AddSpentHoursTab;
